import difference from 'lodash/difference.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';

import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';

export default {
    state: {
        selectedTimePeriodIds: [],
        selectedTimePeriodTemplateIds: [],

        /**
         * {
         *     mapped: {
         *         sectionId: {
         *             1: [...],
         *             2: [...],
         *             ...
         *         }
         *         sectionId: {
         *             1: [...],
         *             2: [...],
         *             ...
         *         }
         *     },
         *     weeks: {
         *         sectionId: [...],
         *         sectionId: [...],
         *     },
         *     days: {
         *         1: [...],
         *         2: [...],
         *         ...
         *     }
         * }
         */
        structure: {
            all: [],
            days: {
                1: [],
                2: [],
                3: [],
                4: [],
                5: [],
                6: [],
                7: [],
            },
            weeks: [],
            past: [],
        },
    },

    getters: {
        timePeriodIsSelected(state) {
            return timePeriodId => state.selectedTimePeriodIds.includes(timePeriodId);
        },

        selectedTimePeriodIds(state) {
            return state.selectedTimePeriodIds;
        },

        timePeriodTemplateIsSelected(state) {
            return timePeriodTemplateId => state.selectedTimePeriodTemplateIds.includes(timePeriodTemplateId);
        },

        selectedTimePeriodTemplateIds(state) {
            return state.selectedTimePeriodTemplateIds;
        },

        selectedAll(state) {
            if (!state.structure) {
                return false;
            }

            return state.structure.all.length !== 0 && state.selectedTimePeriodIds === state.structure.all;
        },

        selectedAllTemplates(state) {
            if (!state.structure) {
                return false;
            }

            return state.structure.all.length !== 0 && state.selectedTimePeriodTemplateIds === state.structure.all;
        },

        selectedAllOfWeek(state) {
            return sectionId => {
                if (Array.isArray(state.structure.weeks) && state.structure.weeks.length === 0) {
                    return false;
                }

                return (
                    difference(state.structure.weeks[sectionId], state.selectedTimePeriodIds).length === 0 &&
                    state.structure.weeks[sectionId].length !== 0
                );
            };
        },

        selectedAllOfWeekTemplate(state) {
            return sectionTemplateId => {
                if (Array.isArray(state.structure.weeks) && state.structure.weeks.length === 0) {
                    return false;
                }

                return (
                    difference(state.structure.weeks[sectionTemplateId], state.selectedTimePeriodTemplateIds).length ===
                        0 && state.structure.weeks[sectionTemplateId].length !== 0
                );
            };
        },

        selectedAllOfDay(state) {
            return day =>
                difference(state.structure.days[day], state.selectedTimePeriodIds).length === 0 &&
                state.structure.days[day].length !== 0;
        },

        selectedAllOfDayTemplate(state) {
            return day =>
                difference(state.structure.days[day], state.selectedTimePeriodTemplateIds).length === 0 &&
                state.structure.days[day].length !== 0;
        },
    },

    actions: {
        loadStructure({ commit }, structure) {
            commit('SET_STRUCTURE', structure);
        },

        async refresh({ dispatch }, { courseTemplateId, courseId }) {
            try {
                const endpoint = courseTemplateId ? 'course-templates' : 'courses';
                const id = courseTemplateId || courseId;

                const { data } = await axios.get(`/api/${endpoint}/${id}/attendance-scheduler-structure`, {
                    errorHandle: false,
                });

                dispatch('loadStructure', data.data);
            } catch (e) {
                handleResponseError(e);
            }
        },

        captureResponse({ dispatch }, response) {
            if (has(response, 'data.meta.attendance_scheduler_structure')) {
                dispatch('loadStructure', get(response, 'data.meta.attendance_scheduler_structure'));
            }
        },

        toggleTimePeriods({ commit }, payload) {
            commit('TOGGLE_TIME_PERIODS', payload);
        },

        toggleWeek({ commit }, payload) {
            commit('TOGGLE_WEEK', payload);
        },

        toggleDay({ commit }, payload) {
            commit('TOGGLE_DAY', payload);
        },

        toggleAll({ commit }, value) {
            commit('TOGGLE_ALL', value);
        },

        toggleAllTemplates({ commit }, value) {
            commit('TOGGLE_ALL_TEMPLATES', value);
        },

        clear({ commit }) {
            commit('CLEAR_SELECTED');
        },

        clearTemplates({ commit }) {
            commit('CLEAR_SELECTED_TEMPLATES');
        },

        toggleWeekTemplate({ commit }, payload) {
            commit('TOGGLE_WEEK_TEMPLATE', payload);
        },

        toggleDayTemplate({ commit }, payload) {
            commit('TOGGLE_DAY_TEMPLATE', payload);
        },
    },

    mutations: {
        SET_STRUCTURE(state, structure) {
            state.structure = structure;
        },

        TOGGLE_TIME_PERIODS(state, { timePeriodIds, value }) {
            if (value) {
                state.selectedTimePeriodIds = [...state.selectedTimePeriodIds, ...timePeriodIds];
            } else {
                state.selectedTimePeriodIds = difference(state.selectedTimePeriodIds, timePeriodIds);
            }
        },

        TOGGLE_TIME_PERIOD(state, { timePeriodId, value }) {
            if (value) {
                state.selectedTimePeriodIds.push(timePeriodId);
            } else {
                state.selectedTimePeriodIds = difference(state.selectedTimePeriodIds, [timePeriodId]);
            }
        },

        TOGGLE_ALL(state, value) {
            if (value === undefined) {
                value = state.selectedTimePeriods !== state.structure.all;
            }

            const selectable = state.structure.all.filter(timePeriodId => !state.structure.past.includes(timePeriodId));

            if (value) {
                state.selectedTimePeriodIds = [...state.selectedTimePeriodIds, ...selectable];
            } else {
                state.selectedTimePeriodIds = difference(state.selectedTimePeriodIds, selectable);
            }
        },

        TOGGLE_ALL_TEMPLATES(state, value) {
            if (value === undefined) {
                value = state.selectedTimePeriodTemplates !== state.structure.all;
            }

            if (value) {
                state.selectedTimePeriodTemplateIds = [...state.selectedTimePeriodTemplateIds, ...state.structure.all];
            } else {
                state.selectedTimePeriodTemplateIds = difference(
                    state.selectedTimePeriodTemplateIds,
                    state.structure.all
                );
            }
        },

        CLEAR_SELECTED(state) {
            state.selectedTimePeriodIds = [];
        },

        CLEAR_SELECTED_TEMPLATES(state) {
            state.selectedTimePeriodTemplateIds = [];
        },

        TOGGLE_WEEK(state, { sectionId, value }) {
            const selectable = difference(
                state.structure.weeks[sectionId].filter(timePeriodId => !state.structure.past.includes(timePeriodId)),
                state.structure.past
            );

            if (value) {
                state.selectedTimePeriodIds = [...state.selectedTimePeriodIds, ...selectable];
            } else {
                state.selectedTimePeriodIds = difference(state.selectedTimePeriodIds, selectable);
            }
        },

        TOGGLE_WEEK_TEMPLATE(state, { sectionTemplateId, value }) {
            if (value) {
                state.selectedTimePeriodTemplateIds = [
                    ...state.selectedTimePeriodTemplateIds,
                    ...state.structure.weeks[sectionTemplateId],
                ];
            } else {
                state.selectedTimePeriodTemplateIds = difference(
                    state.selectedTimePeriodTemplateIds,
                    state.structure.weeks[sectionTemplateId]
                );
            }
        },

        TOGGLE_DAY(state, { day, value }) {
            const selectable = difference(
                state.structure.days[day].filter(timePeriodId => !state.structure.past.includes(timePeriodId)),
                state.structure.past
            );

            if (value) {
                state.selectedTimePeriodIds = [...state.selectedTimePeriodIds, ...selectable];
            } else {
                state.selectedTimePeriodIds = difference(state.selectedTimePeriodIds, selectable);
            }
        },

        TOGGLE_DAY_TEMPLATE(state, { day, value }) {
            if (value) {
                state.selectedTimePeriodTemplateIds = [
                    ...state.selectedTimePeriodTemplateIds,
                    ...state.structure.days[day],
                ];
            } else {
                state.selectedTimePeriodTemplateIds = difference(
                    state.selectedTimePeriodTemplateIds,
                    state.structure.days[day]
                );
            }
        },

        TOGGLE_TIME_PERIOD_TEMPLATES(state, { timePeriodTemplateIds, value }) {
            if (value) {
                state.selectedTimePeriodTemplateIds = [
                    ...state.selectedTimePeriodTemplateIds,
                    ...timePeriodTemplateIds,
                ];
            } else {
                state.selectedTimePeriodTemplateIds = difference(
                    state.selectedTimePeriodTemplateIds,
                    timePeriodTemplateIds
                );
            }
        },

        TOGGLE_TIME_PERIOD_TEMPLATE(state, { timePeriodTemplateId, value }) {
            if (value) {
                state.selectedTimePeriodTemplateIds.push(timePeriodTemplateId);
            } else {
                state.selectedTimePeriodTemplateIds = difference(state.selectedTimePeriodTemplateIds, [
                    timePeriodTemplateId,
                ]);
            }
        },
    },
};
